import React, { useState, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { 
  Bus, 
  Search, 
  Clock, 
  MapPin, 
  ArrowRight, 
  Building2, 
  HelpCircle, 
  ChevronDown, 
  Compass, 
  Layers
} from 'lucide-react';
import SEO from '../components/SEO';
import BusDisclaimer from '../components/BusDisclaimer';
import rawData from '../data/busTimeTableData.json';
import routesData from '../data/processedBusRoutes.json';
import { ENQUIRY_NUMBER } from '../config/busConfig';
import { SITE_URL } from '../config/seo';

export default function BusTimeTableTohanaHub() {
  const [searchTerm, setSearchTerm] = useState('');
  const [filterType, setFilterType] = useState('ALL'); // ALL, ORIGINATING, THROUGH
  const [selectedDest, setSelectedDest] = useState('ALL');
  const [openFaqIndex, setOpenFaqIndex] = useState(0);

  // Filter 337 Tohana records
  const tohanaBuses = useMemo(() => {
    return rawData.timetable.filter((r) => r.bus_stand === 'Tohana');
  }, []);

  // Filter routes originating at Tohana
  const tohanaRoutes = useMemo(() => {
    return routesData.routes.filter((r) => r.bus_stand === 'Tohana' && r.route_type === 'direct');
  }, []);

  const popularDests = ['Hisar', 'Fatehabad', 'Kaithal', 'Narwana', 'Chandigarh', 'Uklana'];

  const filteredBuses = useMemo(() => {
    return tohanaBuses.filter((b) => {
      if (filterType === 'ORIGINATING' && b.from !== 'Tohana') return false;
      if (filterType === 'THROUGH' && b.from === 'Tohana') return false;
      if (selectedDest !== 'ALL' && b.to !== selectedDest) return false;

      if (searchTerm.trim()) {
        const q = searchTerm.toLowerCase();
        const matchTo = b.to.toLowerCase().includes(q);
        const matchFrom = b.from.toLowerCase().includes(q);
        const matchVia = b.via.toLowerCase().includes(q);
        const matchTime = b.departure_time.toLowerCase().includes(q);
        const matchOp = b.operator.toLowerCase().includes(q);
        if (!matchTo && !matchFrom && !matchVia && !matchTime && !matchOp) return false;
      }
      return true;
    }).sort((a, b) => a.departure_time.localeCompare(b.departure_time));
  }, [tohanaBuses, filterType, selectedDest, searchTerm]);

  const pageTitle = 'Tohana Bus Stand Time Table 2026 - Haryana Roadways';
  const metaDesc = 'Tohana bus stand time table 2026: 337 daily Haryana Roadways buses to Hisar, Fatehabad & Kaithal. Check official departure schedule, route stops and enquiry.';

  const breadcrumbJsonLd = {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
      {
        '@type': 'ListItem',
        position: 1,
        name: 'Home',
        item: `${SITE_URL}/`
      },
      {
        '@type': 'ListItem',
        position: 2,
        name: 'Bus Time Table',
        item: `${SITE_URL}/bus-time-table/`
      },
      {
        '@type': 'ListItem',
        position: 3,
        name: 'Tohana Bus Stand Time Table',
        item: `${SITE_URL}/bus-time-table/tohana/`
      }
    ]
  };

  const faqData = [
    {
      q: 'टोहाना बस स्टैंड से मुख्य रूप से कहाँ की बसें मिलती हैं?',
      a: 'टोहाना बस स्टैंड से हिसार (97 बसें), फतेहाबाद (93 बसें), कैथल (40 बसें), नरवाना (27 बसें), चंडीगढ़ (11 बसें), उकलाना (10 बसें) सहित पंजाब व हरियाणा के प्रमुख शहरों के लिए 337 से अधिक दैनिक बस सेवाएं उपलब्ध हैं।'
    },
    {
      q: 'टोहाना से हिसार के लिए पहली और आखिरी बस का समय क्या है?',
      a: 'टोहाना से हिसार के लिए पहली बस सुबह 05:30 बजे तथा दिन की अंतिम बस शाम 18:49 बजे रवाना होती है। यह बसें via बरवाला होकर जाती हैं।'
    },
    {
      q: 'टोहाना से फतेहाबाद के लिए कितनी बसें चलती हैं?',
      a: 'टोहाना से फतेहाबाद के बीच प्रतिदिन 93 बस सेवाएं उपलब्ध हैं। पहली बस सुबह 05:20 बजे और अंतिम बस 18:39 बजे via रतिया प्रस्थान करती है।'
    },
    {
      q: 'टोहाना बस स्टैंड पूछताछ (Enquiry) नंबर क्या है?',
      a: `टोहाना और फतेहाबाद डिपो की आधिकारिक पूछताछ का संपर्क नंबर ${ENQUIRY_NUMBER} है।`
    }
  ];

  const faqJsonLd = {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: faqData.map((f) => ({
      '@type': 'Question',
      name: f.q,
      acceptedAnswer: {
        '@type': 'Answer',
        text: f.a
      }
    }))
  };

  const webPageJsonLd = {
    '@context': 'https://schema.org',
    '@type': 'WebPage',
    name: pageTitle,
    description: metaDesc,
    url: `${SITE_URL}/bus-time-table/tohana/`,
    datePublished: '2026-10-18',
    dateModified: '2026-10-18',
    inLanguage: ['hi', 'en']
  };

  return (
    <div className="min-h-screen bg-slate-50/60 dark:bg-gray-950 text-gray-800 dark:text-gray-200">
      <SEO
        title={pageTitle}
        titleTemplate=""
        description={metaDesc}
        canonical="/bus-time-table/tohana/"
        jsonLd={[breadcrumbJsonLd, faqJsonLd, webPageJsonLd]}
      />

      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-6 sm:py-10 space-y-8">
        {/* Breadcrumbs */}
        <nav aria-label="Breadcrumb" className="text-xs sm:text-sm text-gray-500 dark:text-gray-400">
          <ol className="flex items-center gap-1.5 sm:gap-2">
            <li>
              <Link to="/" className="hover:text-blue-600 dark:hover:text-blue-400 transition">
                Home
              </Link>
            </li>
            <li>/</li>
            <li>
              <Link to="/bus-time-table" className="hover:text-blue-600 dark:hover:text-blue-400 transition">
                Bus Time Table
              </Link>
            </li>
            <li>/</li>
            <li className="font-medium text-gray-900 dark:text-gray-100">
              Tohana Bus Stand Time Table
            </li>
          </ol>
        </nav>

        {/* Hero Card */}
        <div className="bg-gradient-to-br from-emerald-900 via-teal-900 to-slate-900 text-white rounded-3xl p-6 sm:p-10 shadow-lg relative overflow-hidden">
          <div className="relative z-10 space-y-4 max-w-3xl">
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-emerald-500/20 text-emerald-200 text-xs font-semibold uppercase tracking-wider border border-emerald-400/30">
              <Bus className="h-3.5 w-3.5" />
              Haryana Roadways Tohana Sub-Depot / Bus Stand
            </div>
            <h1 className="text-2xl sm:text-4xl font-extrabold tracking-tight">
              Tohana Bus Stand Time Table 2026 (टोहाना बस समय सारिणी)
            </h1>
            <p className="text-sm sm:text-base text-emerald-100/90 leading-relaxed">
              हरियाणा रोडवेज फतेहाबाद डिपो के अंतर्गत टोहाना बस स्टैंड से चलने वाली सभी 337 दैनिक बसों की विस्तृत समय सारिणी। हिसार, फतेहाबाद, कैथल, नरवाना, चंडीगढ़ और लोकल रूटों के प्रस्थान समय प्राप्त करें।
            </p>

            <div className="flex flex-wrap items-center gap-3 pt-2 text-xs sm:text-sm">
              <span className="bg-white/10 backdrop-blur px-3 py-1 rounded-lg border border-white/10 font-semibold">
                🚌 337 Total Buses
              </span>
              <span className="bg-white/10 backdrop-blur px-3 py-1 rounded-lg border border-white/10 font-semibold">
                ⚡ 97 Buses to Hisar
              </span>
              <span className="bg-white/10 backdrop-blur px-3 py-1 rounded-lg border border-white/10 font-semibold">
                🔄 93 Buses to Fatehabad
              </span>
              <span className="bg-white/10 backdrop-blur px-3 py-1 rounded-lg border border-white/10 font-semibold">
                🌾 13 Local Routes
              </span>
            </div>
          </div>
        </div>

        {/* Disclaimer */}
        <BusDisclaimer />

        {/* Quick Links Card */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <Link
            to="/bus-time-table/tohana-local-routes"
            className="group bg-white dark:bg-gray-900 rounded-2xl p-5 border border-gray-200 dark:border-gray-800 hover:border-emerald-500 dark:hover:border-emerald-500 transition shadow-sm hover:shadow-md"
          >
            <div className="flex items-center justify-between mb-3">
              <div className="p-2.5 rounded-xl bg-teal-50 dark:bg-teal-950/50 text-teal-600 dark:text-teal-400">
                <Compass className="h-5 w-5" />
              </div>
              <span className="text-xs font-bold text-teal-700 dark:text-teal-400 bg-teal-100 dark:bg-teal-950/80 px-2.5 py-0.5 rounded-full">
                23 Buses / 13 Routes
              </span>
            </div>
            <h3 className="text-base font-bold text-gray-900 dark:text-white group-hover:text-emerald-600 dark:group-hover:text-emerald-400 transition">
              Tohana Local & Rural Routes
            </h3>
            <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
              टोहाना से ग्रामीण व कस्बा रूट (Patran, Dhanouri, Dhamthan, Barwala, Sangrur, etc.)
            </p>
            <div className="mt-3 text-xs font-semibold text-emerald-600 dark:text-emerald-400 flex items-center gap-1 group-hover:translate-x-1 transition-transform">
              View Tohana Local Routes <ArrowRight className="h-3.5 w-3.5" />
            </div>
          </Link>

          <Link
            to="/bus-time-table"
            className="group bg-white dark:bg-gray-900 rounded-2xl p-5 border border-gray-200 dark:border-gray-800 hover:border-blue-500 dark:hover:border-blue-500 transition shadow-sm hover:shadow-md"
          >
            <div className="flex items-center justify-between mb-3">
              <div className="p-2.5 rounded-xl bg-blue-50 dark:bg-blue-950/50 text-blue-600 dark:text-blue-400">
                <Building2 className="h-5 w-5" />
              </div>
              <span className="text-xs font-bold text-blue-700 dark:text-blue-400 bg-blue-100 dark:bg-blue-950/80 px-2.5 py-0.5 rounded-full">
                526 Buses
              </span>
            </div>
            <h3 className="text-base font-bold text-gray-900 dark:text-white group-hover:text-blue-600 dark:group-hover:text-blue-400 transition">
              Fatehabad Main Bus Stand Time Table
            </h3>
            <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
              फतेहाबाद मुख्य बस स्टैंड की संपूर्ण समय सारिणी (Delhi, Chandigarh, Village Routes)
            </p>
            <div className="mt-3 text-xs font-semibold text-blue-600 dark:text-blue-400 flex items-center gap-1 group-hover:translate-x-1 transition-transform">
              View Fatehabad Hub <ArrowRight className="h-3.5 w-3.5" />
            </div>
          </Link>
        </div>

        {/* 6 Major Tohana Direct Routes */}
        <section aria-labelledby="tohana-major-routes" className="space-y-4">
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2 border-b border-gray-200 dark:border-gray-800 pb-3">
            <div>
              <h2 id="tohana-major-routes" className="text-xl sm:text-2xl font-bold text-gray-900 dark:text-white">
                Major Bus Routes from Tohana (टोहाना से मुख्य बस रूट)
              </h2>
              <p className="text-xs sm:text-sm text-gray-500 dark:text-gray-400">
                Direct route timetable pages with dedicated via breakdown and departure schedules
              </p>
            </div>
            <span className="text-xs font-semibold text-emerald-600 dark:text-emerald-400">
              {tohanaRoutes.length} Direct Routes
            </span>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {tohanaRoutes.map((r) => (
              <Link
                key={r.slug}
                to={`/bus-time-table/${r.slug}`}
                className="group bg-white dark:bg-gray-900 rounded-xl p-5 border border-gray-200 dark:border-gray-800 hover:border-emerald-500 dark:hover:border-emerald-500 transition shadow-sm hover:shadow"
              >
                <div className="flex items-center justify-between text-xs text-gray-500 dark:text-gray-400 mb-2">
                  <span className="font-bold text-emerald-700 dark:text-emerald-400 bg-emerald-50 dark:bg-emerald-950/60 px-2 py-0.5 rounded">
                    {r.total_buses} Buses Daily
                  </span>
                  <span className="font-mono text-gray-600 dark:text-gray-300">
                    {r.first_bus} - {r.last_bus}
                  </span>
                </div>
                <h3 className="font-bold text-base text-gray-900 dark:text-white group-hover:text-emerald-600 dark:group-hover:text-emerald-400 transition">
                  {r.from} to {r.to}
                  <span className="block text-xs font-normal text-gray-500 dark:text-gray-400 mt-0.5">
                    ({r.from_hi} से {r.to_hi})
                  </span>
                </h3>
                <p className="text-xs text-gray-500 dark:text-gray-400 mt-2 truncate">
                  Via: {r.via_points.join(', ')}
                </p>
                <div className="mt-3 pt-3 border-t border-gray-100 dark:border-gray-800 flex items-center justify-between text-xs font-semibold text-emerald-600 dark:text-emerald-400">
                  <span>View Timetable</span>
                  <ArrowRight className="h-3.5 w-3.5 group-hover:translate-x-1 transition-transform" />
                </div>
              </Link>
            ))}
          </div>
        </section>

        {/* Master Timetable Section */}
        <section aria-labelledby="tohana-departures-heading" className="bg-white dark:bg-gray-900 rounded-2xl border border-gray-200 dark:border-gray-800 shadow-sm overflow-hidden">
          <div className="p-5 sm:p-6 border-b border-gray-200 dark:border-gray-800 space-y-4">
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
              <div>
                <h2 id="tohana-departures-heading" className="text-xl font-bold text-gray-900 dark:text-white flex items-center gap-2">
                  <Clock className="h-5 w-5 text-emerald-600" />
                  All Tohana Departures Directory (टोहाना प्रस्थान समय सारिणी)
                </h2>
                <p className="text-xs sm:text-sm text-gray-500 dark:text-gray-400 mt-0.5">
                  Showing {filteredBuses.length} of {tohanaBuses.length} buses departing Tohana Bus Stand
                </p>
              </div>

              <div className="relative w-full sm:w-72">
                <Search className="absolute left-3 top-2.5 h-4 w-4 text-gray-400" />
                <input
                  type="text"
                  placeholder="Search destination, via, time..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-full pl-9 pr-3 py-2 text-xs sm:text-sm rounded-xl border border-gray-200 dark:border-gray-700 bg-slate-50 dark:bg-gray-800 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-emerald-500"
                />
              </div>
            </div>

            <div className="flex flex-wrap items-center justify-between gap-3 pt-2 border-t border-gray-100 dark:border-gray-800">
              <div className="flex items-center gap-1.5">
                <span className="text-xs font-semibold text-gray-500 dark:text-gray-400 mr-1">
                  Type:
                </span>
                {[
                  { id: 'ALL', label: `All (${tohanaBuses.length})` },
                  { id: 'ORIGINATING', label: 'Originating (301)' },
                  { id: 'THROUGH', label: 'Through (36)' }
                ].map((t) => (
                  <button
                    key={t.id}
                    type="button"
                    onClick={() => setFilterType(t.id)}
                    className={`px-3 py-1 rounded-lg text-xs font-semibold transition ${
                      filterType === t.id
                        ? 'bg-emerald-600 text-white shadow-sm'
                        : 'bg-gray-100 text-gray-700 hover:bg-gray-200 dark:bg-gray-800 dark:text-gray-300'
                    }`}
                  >
                    {t.label}
                  </button>
                ))}
              </div>

              <div className="flex flex-wrap items-center gap-1">
                <span className="text-xs font-semibold text-gray-500 dark:text-gray-400 mr-1">
                  Dest:
                </span>
                <button
                  type="button"
                  onClick={() => setSelectedDest('ALL')}
                  className={`px-2 py-0.5 rounded text-xs transition ${
                    selectedDest === 'ALL'
                      ? 'bg-gray-900 text-white dark:bg-white dark:text-gray-900 font-bold'
                      : 'bg-slate-100 text-gray-600 dark:bg-gray-800 dark:text-gray-400'
                  }`}
                >
                  All
                </button>
                {popularDests.map((d) => (
                  <button
                    key={d}
                    type="button"
                    onClick={() => setSelectedDest(selectedDest === d ? 'ALL' : d)}
                    className={`px-2 py-0.5 rounded text-xs transition ${
                      selectedDest === d
                        ? 'bg-gray-900 text-white dark:bg-white dark:text-gray-900 font-bold'
                        : 'bg-slate-100 text-gray-600 dark:bg-gray-800 dark:text-gray-400'
                    }`}
                  >
                    {d}
                  </button>
                ))}
              </div>
            </div>
          </div>

          <div className="overflow-x-auto max-h-[600px] overflow-y-auto">
            <table className="w-full text-left border-collapse text-xs sm:text-sm">
              <caption className="sr-only">Schedule of buses departing Tohana Bus Stand</caption>
              <thead className="sticky top-0 bg-slate-100 dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700 z-10">
                <tr className="text-gray-600 dark:text-gray-300 font-semibold">
                  <th scope="col" className="py-3 px-4 sm:px-6 w-14 text-center">#</th>
                  <th scope="col" className="py-3 px-4 sm:px-6">Departure Time</th>
                  <th scope="col" className="py-3 px-4 sm:px-6">Origin</th>
                  <th scope="col" className="py-3 px-4 sm:px-6">Destination</th>
                  <th scope="col" className="py-3 px-4 sm:px-6">Via Route</th>
                  <th scope="col" className="py-3 px-4 sm:px-6">Type</th>
                  <th scope="col" className="py-3 px-4 sm:px-6">Operator</th>
                  <th scope="col" className="py-3 px-4 sm:px-6 text-center">Days</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100 dark:divide-gray-800">
                {filteredBuses.map((bus, idx) => (
                  <tr key={`${bus.serial_no}-${idx}`} className="hover:bg-emerald-50/40 dark:hover:bg-emerald-950/20 transition-colors">
                    <td className="py-2.5 px-4 sm:px-6 text-center font-mono text-gray-400">
                      {idx + 1}
                    </td>
                    <td className="py-2.5 px-4 sm:px-6 font-mono font-bold text-sm text-gray-900 dark:text-white">
                      <span className="inline-flex items-center gap-1 text-emerald-700 dark:text-emerald-300 bg-emerald-50 dark:bg-emerald-950/50 px-2 py-0.5 rounded">
                        <Clock className="h-3 w-3" />
                        {bus.departure_time}
                      </span>
                    </td>
                    <td className="py-2.5 px-4 sm:px-6 font-medium text-gray-900 dark:text-white">
                      {bus.from}
                    </td>
                    <td className="py-2.5 px-4 sm:px-6 font-semibold text-gray-900 dark:text-white">
                      {bus.to}
                    </td>
                    <td className="py-2.5 px-4 sm:px-6 text-gray-600 dark:text-gray-400">
                      {bus.via}
                    </td>
                    <td className="py-2.5 px-4 sm:px-6">
                      <span className="inline-block px-2 py-0.5 rounded text-xs font-semibold bg-slate-100 text-slate-700 dark:bg-gray-800 dark:text-gray-300">
                        {bus.service_type}
                      </span>
                    </td>
                    <td className="py-2.5 px-4 sm:px-6 font-bold text-xs">
                      {bus.operator}
                    </td>
                    <td className="py-2.5 px-4 sm:px-6 text-center text-xs text-gray-500">
                      {bus.service_days || 'All Days'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>

        {/* Tohana FAQs */}
        <section aria-labelledby="tohana-faqs-heading" className="bg-white dark:bg-gray-900 rounded-2xl border border-gray-200 dark:border-gray-800 p-6 sm:p-8 shadow-sm space-y-4">
          <div className="flex items-center gap-2 border-b border-gray-100 dark:border-gray-800 pb-4">
            <HelpCircle className="h-6 w-6 text-emerald-600" />
            <h2 id="tohana-faqs-heading" className="text-xl sm:text-2xl font-bold text-gray-900 dark:text-white">
              Tohana Bus Stand FAQs (अक्सर पूछे जाने वाले सवाल)
            </h2>
          </div>

          <div className="space-y-3">
            {faqData.map((f, i) => {
              const isOpen = openFaqIndex === i;
              return (
                <div key={i} className="rounded-xl border border-gray-200 dark:border-gray-800 overflow-hidden">
                  <button
                    type="button"
                    onClick={() => setOpenFaqIndex(isOpen ? -1 : i)}
                    className="w-full flex items-center justify-between p-4 text-left font-semibold text-gray-900 dark:text-white hover:bg-slate-50 dark:hover:bg-gray-800/50 transition gap-3"
                  >
                    <span>{f.q}</span>
                    <ChevronDown className={`h-5 w-5 text-gray-500 transition-transform ${isOpen ? 'rotate-180 text-emerald-600' : ''}`} />
                  </button>
                  {isOpen && (
                    <div className="px-4 pb-4 pt-1 text-xs sm:text-sm text-gray-600 dark:text-gray-300 border-t border-gray-100 dark:border-gray-800 bg-slate-50/50 dark:bg-gray-800/30">
                      {f.a}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </section>
      </div>
    </div>
  );
}
